import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Palette, Sparkles } from 'lucide-react';
import { colorOptions, advisorInfo } from '../data/mock';

const ColorsSection = () => {
  const getColorClass = (colorName) => {
    const colorMap = {
      'Ivory White': 'bg-slate-100 border-2 border-slate-300',
      'Glittering Silver': 'bg-gray-300 border-2 border-gray-400',
      'Lava Red': 'bg-red-600 border-2 border-red-700',
      'Granite Grey': 'bg-gray-600 border-2 border-gray-700',
      'Cobalt Blue': 'bg-blue-600 border-2 border-blue-700',
      'Electric Blue': 'bg-cyan-500 border-2 border-cyan-600',
      'Passion Red': 'bg-red-500 border-2 border-red-600',
      'Glitter Black': 'bg-gray-900 border-2 border-black',
      'Pearl Diamond White': 'bg-gradient-to-br from-white to-gray-100 border-2 border-gray-300',
      'Pearl Delima Red': 'bg-gradient-to-br from-red-400 to-red-600 border-2 border-red-700',
      'Pearl Electric Blue': 'bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-blue-700'
    };
    return colorMap[colorName] || 'bg-gray-300 border-2 border-gray-400';
  };

  const getCategoryIcon = (category) => {
    if (category.includes('Premium')) return <Sparkles className="w-5 h-5 text-yellow-500" />;
    if (category.includes('Metallic')) return <Palette className="w-5 h-5 text-purple-500" />;
    return <div className="w-5 h-5 rounded-full bg-gray-400"></div>;
  };

  return (
    <section id="colors" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-purple-100 text-purple-800 text-sm font-medium px-4 py-2 rounded-full mb-4">
            <Palette className="w-4 h-4 mr-2" />
            Color Options
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Pilihan Warna 
            <span className="text-purple-600"> Menarik</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Dapatkan kereta impian anda dalam pelbagai pilihan warna yang menarik
          </p>
          <p className="text-sm text-slate-500 mt-2">
            *Warna bergantung pada model & variant
          </p>
        </div>

        {/* Color Categories */}
        <div className="grid lg:grid-cols-3 gap-8">
          {colorOptions.map((category, categoryIndex) => (
            <Card key={categoryIndex} className="bg-white rounded-2xl shadow-lg border-0 overflow-hidden hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6">
                <CardTitle className="flex items-center space-x-3 text-xl">
                  {getCategoryIcon(category.category)}
                  <span>{category.category}</span>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  {category.colors.map((color, colorIndex) => (
                    <div key={colorIndex} className="group cursor-pointer">
                      <div className="relative">
                        {/* Color Circle */}
                        <div className={`w-16 h-16 rounded-full mx-auto mb-3 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl ${getColorClass(color)}`}>
                          {color.includes('Pearl') && (
                            <div className="absolute inset-0 rounded-full bg-white bg-opacity-20 animate-pulse"></div>
                          )}
                        </div>
                        
                        {/* Color Name */}
                        <div className="text-center">
                          <p className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                            {color}
                          </p>
                          {color.includes('Pearl') && (
                            <Badge className="mt-1 bg-yellow-100 text-yellow-800 text-xs px-2 py-1">
                              Premium
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Category Note */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 text-center">
                    {category.category === 'Premium (AV model)' 
                      ? 'Eksklusif untuk variant AV sahaja'
                      : category.category === 'Metallic'
                      ? 'Warna metalik dengan kilau istimewa'
                      : 'Warna asas yang klasik dan elegan'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
            <div className="text-6xl mb-4">🎨</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Tak Pasti Warna Mana Yang Sesuai?
            </h3>
            <p className="text-slate-600 mb-6">
              Hubungi Syasyah untuk mendapatkan nasihat warna terbaik mengikut model dan variant pilihan anda
            </p>
            <button 
              onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, saya nak tanya tentang pilihan warna untuk kereta. Boleh help?')}`, '_blank')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Palette className="w-5 h-5 mr-3 inline" />
              Tanya Tentang Warna
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ColorsSection;